// Modules
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { SbiToastService } from '@shared-module/services/sbi-toast/sbi-toast.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// Rxjs
import { of } from 'rxjs';
import { map, tap, exhaustMap, catchError } from 'rxjs/operators';

// Constants
import { APP_PATHS_CONSTANTS } from '@shared/constants';

// Store - Actions
import * as AuthActions from '@store/actions/auth.actions';
import * as ForcedPasswordChangeActions from '@store/actions/forced-password-change.actions';

// Store - State
import { AppState } from '@store/reducers';

// Services
import { AuthService } from '@services/auth-service/auth.service';
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';

// Components
import { PasswordRecoverySuccessfulComponent } from '@public/components/password-recovery-successful/password-recovery-successful.component';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthEffects {
  // Constructor
  constructor(
    private _app: AppConfigService,
    private _router: Router,
    private readonly _sbiToast: SbiToastService,
    private _actions$: Actions,
    private _store: Store<AppState>,
    private _matDialog$: MatDialog,
    private _authService: AuthService,
    private _authMsalService: MsalService
  ) {}

  // Different auth endpoints return the validation message differently —
  // some as a plain-text body, some as JSON ({ errorMessage: string }).
  // Support both so we don't hardcode one shape per endpoint.
  private _extractFieldError(err: any): string | undefined {
    // Plain-text body returned directly as the error.
    if (typeof err?.error === 'string') {
      return err.error.trim() || undefined;
    }
    // Some endpoints reply with plain text while HttpClient expects JSON; the
    // raw body then lands in err.error.text after the parse failure.
    if (typeof err?.error?.text === 'string') {
      return err.error.text.trim() || undefined;
    }
    // JSON-shaped error ({ errorMessage: string }).
    if (typeof err?.error?.errorMessage === 'string') {
      return err.error.errorMessage.trim() || undefined;
    }
    return undefined;
  }

  // User login (Microsoft/MSAL via get-token)
  userLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.loginMSTokenRequested),
      exhaustMap(action =>
        this._authService.loginWithMSALToken(action.payload).pipe(
          map(response => {
            // Check if forced password change is required
            if (response.mustChangePassword) {
              this._store.dispatch(
                ForcedPasswordChangeActions.forcedPasswordChangePending({
                  payload: { challengeId: response.passwordChangeChallengeId },
                })
              );
              this._router.navigateByUrl('/forced-password-change');
              return AuthActions.loginMSTokenSuccess({ payload: null });
            }

            // Get user
            const user = {
              email: response.email,
              accessToken: response.accessToken.token,
              refreshToken: response.refreshToken,
            };

            // Save in the session storage the user
            sessionStorage.setItem(
              this._app.loggedUserSessionKey,
              JSON.stringify(user)
            );

            // Navigate to private area
            this._router.navigateByUrl(
              APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER
            );

            // Return the action
            return AuthActions.loginMSTokenSuccess({ payload: user });
          }),
          catchError(err => {
            if (err.status === 400 && err.error) {
              this._sbiToast.error(err.error);
            }
            this._authMsalService.logoutPopup();
            return of(AuthActions.loginMSTokenFailure());
          })
        )
      )
    )
  );

  // Credential login (email + password)
  credentialLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.loginRequested),
      exhaustMap(action =>
        this._authService
          .login(
            action.payload.credential,
            action.payload.credentialType,
            action.payload.password,
            action.payload.portal
          )
          .pipe(
            map(response => {
              // Check if forced password change is required
              if (response.mustChangePassword) {
                // mustChangePassword: true — do NOT save session, redirect to forced change page
                this._store.dispatch(
                  ForcedPasswordChangeActions.forcedPasswordChangePending({
                    payload: { challengeId: response.passwordChangeChallengeId },
                  })
                );
                this._router.navigateByUrl('/forced-password-change');
                return AuthActions.loginSuccessful({ payload: null });
              }

              // Normal flow: build user from response
              const user = {
                email: response.email,
                accessToken: response.accessToken?.token || response.accessToken,
                refreshToken: response.refreshToken,
              };

              // Save session and navigate to private area
              sessionStorage.setItem(
                this._app.loggedUserSessionKey,
                JSON.stringify(user)
              );
              this._router.navigateByUrl(
                APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER
              );

              return AuthActions.loginSuccessful({ payload: user });
            }),
            catchError(err => {
              if (err.status === 400 && err.error) {
                this._sbiToast.error(
                  typeof err.error === 'string'
                    ? err.error
                    : 'No se pudo completar el inicio de sesión. Intentá nuevamente.'
                );
              } else {
                this._sbiToast.error(
                  'No se pudo completar el inicio de sesión. Intentá nuevamente.'
                );
              }
              return of(AuthActions.loginFailed());
            })
          )
      )
    )
  );

  // Login Microsoft
  loginMicrosoft$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.loginMicrosoftRequested),
      exhaustMap(() =>
        this._authMsalService.loginPopup().pipe(
          map(() => {
            return AuthActions.loginMicrosoftSuccess();
          }),
          catchError(err =>
            of(AuthActions.loginMicrosoftFailure({ payload: err }))
          )
        )
      )
    )
  );

  // Logout user
  userLogout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logoutRequested),
      exhaustMap(() =>
        this._authService.logout().pipe(
          map(() => AuthActions.logoutSuccessful()),
          tap(() => {
            const currentAccount =
              this._authMsalService.instance.getAllAccounts()[0];
            const logoutHint = currentAccount.idTokenClaims.login_hint;
            this._authMsalService.logoutPopup({
              mainWindowRedirectUri: this._app.microsoft365.redirectUri,
              logoutHint: logoutHint,
            });
          }),
          catchError(() => of(AuthActions.logoutSuccessful()))
        )
      )
    )
  );

  // Logout success
  userLogoutSuccessful$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.logoutSuccessful),
        map(() => {
          // Close all dialogs
          this._matDialog$.closeAll();

          //FIXME LATER
          // this._router.navigateByUrl('/');
        })
      ),
    { dispatch: false }
  );

  // Password recovery request init
  passwordRecoveryRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.passwordRecoveryRequested),
      exhaustMap(action =>
        this._authService.requestPasswordRecovery(action.payload.email).pipe(
          map(() => AuthActions.passwordRecoveryResponse()),
          catchError(() => of(AuthActions.passwordRecoveryResponse()))
        )
      )
    )
  );

  // Password recovery successful
  passwordRecoverySuccessful$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.passwordRecoveryResponse),
        tap(() => {
          this._matDialog$.closeAll();
          this._matDialog$.open(PasswordRecoverySuccessfulComponent);
        })
      ),
    { dispatch: false }
  );

  // Password recovery key validation
  passwordRecoveryKeyValidation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.verifyPasswordResetRequested),
      exhaustMap(action =>
        this._authService
          .checkPasswordRecoveryValidity(action.payload.key)
          .pipe(
            map(response =>
              AuthActions.verifyPasswordResetResponse({
                payload: { invalid: !response.isValid },
              })
            ),
            catchError(() =>
              of(
                AuthActions.verifyPasswordResetResponse({
                  payload: { invalid: true },
                })
              )
            )
          )
      )
    )
  );

  // Password reset
  passwordReset$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.passwordResetRequested),
      exhaustMap(action =>
        this._authService
          .passwordRecoveryChange(
            action.payload.key,
            action.payload.newPassword
          )
          .pipe(
            tap(() =>
              this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_LOGIN)
            ),
            map(() => AuthActions.passwordResetSuccessful(null)),
            catchError(err =>
              of(
                AuthActions.passwordResetFailed({
                  payload: { fieldError: this._extractFieldError(err) },
                })
              )
            )
          )
      )
    )
  );

  // Password reset failed - a field-level message means the field already
  // shows it; only toast when there's no specific message to show under the field.
  passwordResetFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.passwordResetFailed),
        tap(action => {
          if (!action.payload?.fieldError) {
            this._sbiToast.error(
              'No pudimos actualizar la contraseña. Intentá nuevamente.'
            );
          }
        })
      ),
    { dispatch: false }
  );

  // Password reset successful - show success toast
  passwordResetSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.passwordResetSuccessful),
        tap(() => {
          this._sbiToast.success('La contraseña se actualizó correctamente');
        })
      ),
    { dispatch: false }
  );

  // Password change
  passwordChangeRequested$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.passwordChangeRequested),
      exhaustMap(action =>
        this._authService
          .changePassword(
            action.payload.email,
            action.payload.newPassword,
            action.payload.currentPassword
          )
          .pipe(
            map(() => AuthActions.passwordChangeSuccessful()),
            tap(() => this._sbiToast.success('Contraseña cambiada con éxito')),
            catchError(err => {
              const backendMessage = this._extractFieldError(err);
              const lowerMessage = backendMessage?.trim().toLowerCase() ?? '';
              const isNewPasswordError =
                !!backendMessage &&
                (lowerMessage.startsWith('la contraseña') ||
                  lowerMessage.includes('nueva contraseña'));

              const fieldError = isNewPasswordError
                ? backendMessage
                : undefined;

              // No toast: both cases are already shown under their field
              // (fieldError → "Nueva contraseña", invalidCredentials →
              // "Contraseña actual" via password-change.component.ts).

              return of(
                AuthActions.passwordChangeFailed({
                  payload: {
                    invalidCredentials: !isNewPasswordError,
                    fieldError,
                  },
                })
              );
            })
          )
      )
    )
  );

  // Activation key validation
  activationKeyValidation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.verifyActivationRequested),
      exhaustMap(action =>
        this._authService.checkActivationValidity(action.payload.key).pipe(
          map(response =>
            AuthActions.verifyActivationResponse({
              payload: { invalid: !response.isValid },
            })
          ),
          catchError(() =>
            of(
              AuthActions.verifyActivationResponse({
                payload: { invalid: true },
              })
            )
          )
        )
      )
    )
  );

  // User activation
  userActivation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.activationRequested),
      exhaustMap(action =>
        this._authService
          .activateUser(action.payload.key, action.payload.password)
          .pipe(
            tap(() => {
              this._sbiToast.success('Cuenta activada con éxito');
              this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_LOGIN);
            }),
            map(() => AuthActions.activationSuccessful(null)),
            catchError(err =>
              of(
                AuthActions.activationFailed({
                  payload: { fieldError: this._extractFieldError(err) },
                })
              )
            )
          )
      )
    )
  );

  // Activation failed - a field-level message means the field already shows
  // it; only toast when there's no specific message to show under the field.
  activationFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.activationFailed),
        tap(action => {
          if (!action.payload?.fieldError) {
            this._sbiToast.error(
              'No pudimos activar la cuenta. Intentá nuevamente.'
            );
          }
        })
      ),
    { dispatch: false }
  );

  // Get Config Listado
  getConfigListado$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.getConfigListadoRequested),
      exhaustMap(action =>
        this._authService.getConfigListados(action.payload.key).pipe(
          map(response => {
            const r = AuthActions.getConfigListadoResponse({
              payload: {
                invalid: false,
                key: response[0].key,
                config: JSON.parse(response[0].config),
              },
            });
            return r;
          }),
          catchError(() =>
            of(
              AuthActions.getConfigListadoResponse({
                payload: { invalid: true, key: null, config: null },
              })
            )
          )
        )
      )
    )
  );

  // Get Config Listado
  setConfigListado$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.setConfigListadoRequested),
      exhaustMap(action =>
        this._authService
          .setConfigListados(action.payload.key, action.payload.config)
          .pipe(
            map(response => {
              this._sbiToast.success(
                'La configuración de la tabla se guardó correctamente'
              );
              return AuthActions.setConfigListadoResponse({
                payload: { invalid: !response.isValid },
              });
            }),
            catchError(() =>
              of(
                AuthActions.setConfigListadoResponse({
                  payload: { invalid: true },
                })
              )
            )
          )
      )
    )
  );
}
